import { getRemoteFileUrl, useUpload } from "@/api/files";
import { ModalLoadFailed } from "@/components/modals";
import { ArticleForm } from "@/forms/article-form";
import { client } from "@/hono";
import { useFileUpload } from "@msuite/ui/file-upload";
import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { type ArticleFormSchema, articleFormSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";

/** Props Interface */
interface EditArticleModalProps extends ModalProps {
	onComplete?: () => Promise<void>;
	articleId: string;
}

export function EditArticleModal({
	onClose,
	isOpen,
	onComplete,
	articleId,
}: EditArticleModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const qc = useQueryClient();
	const { uploadFn } = useUpload();
	const imageUploadController = useFileUpload({
		uploadFn,
		getRemoteFileUrl,
		defaultFiles: async () => {
			const response = await client.files[":tableName"][":key"][":identifier"].$get({
				param: {
					tableName: "articleFiles",
					key: "articleId",
					identifier: articleId,
				},
			});
			const data = await responseHandler(response);
			return data.files;
		},
	});
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.stock.articles[":articleId"].$get({ param: { articleId } });
		const data = await responseHandler(response);
		return articleFormSchema.parse({ ...data.article, autoGenerateNumber: true });
	});
	const methods = useForm({
		resolver: resolver(articleFormSchema),
		defaultValues,
	});

	/** Functions */
	async function handleOnSubmit(json: ArticleFormSchema) {
		const uploadResults = await imageUploadController.handleOnFileUpload();
		const response = await client.stock.articles[":articleId"].$patch({
			param: { articleId },
			json: {
				...json,
				uploadResults,
			},
		});
		await responseHandler(response);
		// The article page caches its picture for half an hour, and a new upload replaces it.
		await qc.invalidateQueries({ predicate: invalidate(["article-image"]) });
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("stock.edit_article")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<ArticleForm imageUploadController={imageUploadController} />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
